import { Component, Fragment } from 'react'
import { uniqueId } from 'lodash-es'
import Cropper from 'cropperjs'
import Immutable from 'immutable'
import ImmutablePropTypes from 'react-immutable-proptypes'
import PropTypes from 'prop-types'
import cc from 'classcat'

import { getErrorReasonMessage } from '../templateComponents/Workspace/plugins/image/ImageField'
import { mimeTypeMap } from '../templateComponents/Workspace/daliConfig'
import Button from '../formComponents/Button'
import ButtonRow from '../formComponents/ButtonRow'
import compose from '../../utils/compose'
import translate from '../../utils/translate'
import withI18n from '../withI18n'

function dataUriToBlob(uri, type) {
  const byteString = atob(uri.split(',')[1])
  const arrayBuffer = new ArrayBuffer(byteString.length)
  const ia = new Uint8Array(arrayBuffer)
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i)
  }
  return new Blob([arrayBuffer], { type })
}

const aspectRatioMap = {
  '1:1': 1,
  '2:3': 2 / 3,
  '4:3': 4 / 3,
  '16:9': 16 / 9,
  '3:2': 3 / 2,
}

const defaultAspectRatio = aspectRatioMap['3:2']
const zoomRatio = 0.1

function getSanitizedCropSizeValue(inputValue) {
  const numberValue = Number(inputValue.replace(',', '.'))
  const value = numberValue ? Math.round(numberValue) : inputValue.replace(/[^0-9]/g, '')
  return Number(value)
}

function getFilename(src) {
  const decodedSrc = decodeURI(src)
  if (src.startsWith('/storage/images/image?remote=')) {
    const url = decodedSrc.split('storage/images/image?remote=').pop()
    return url.split('/').pop().replace(/\?.+/, '').split('&')[0]
  }

  return decodedSrc.split('/').pop().replace(/\?.+/, '')
}

export class LogoCropSettingsRaw extends Component {
  static propTypes = {
    config: PropTypes.shape({
      storeFile: PropTypes.func.isRequired,
      imageUrl: PropTypes.func,
      previewUrl: PropTypes.func,
    }).isRequired,
    data: ImmutablePropTypes.mapContains({
      src: PropTypes.string.isRequired,
      width: PropTypes.number,
      height: PropTypes.number,
      alt: PropTypes.string,
      link: PropTypes.string,
    }).isRequired,
    onSave: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
    renderLayout: PropTypes.func.isRequired,
  }

  state = {
    canCrop: false,
    cropAspectRatio: defaultAspectRatio,
    cropHeight: 0,
    cropWidth: 0,
    errorMessage: null,
    isCropperReady: false,
    isCropperDestroyed: false,
    lockAspectRatioOnSizeInputFieldChange: true,
  }

  t = (key, ...args) => this.props.t(`components.imageElementComponent.cropModal.${key}`, ...args)

  componentDidMount() {
    this.cropper = new Cropper(this.cropperNode, {
      // Restrict the crop box to not exceed the size of the image canvas
      viewMode: 1,
      // Use dragging mode (no new crop box will be created upon dragging the canvas)
      dragMode: 'move',
      toggleDragModeOnDblclick: false,
      // Set the default aspect ratio
      aspectRatio: defaultAspectRatio,
      // Turn off unnecessary checks
      responsive: false,
      restore: false,
      checkCrossOrigin: false,
      // Disable the dashed lines above the crop box
      guides: false,
      // Enable crop on initialization
      autoCrop: true,
      // Set wheel zoom ratio to have the same value as used with zoom buttons
      wheelZoomRatio: zoomRatio,
      // Set up event handlers
      ready: this.handleCropperReady,
      crop: this.handleCropperCrop,
    })

    this.htmlId = uniqueId('image-crop-')
  }

  componentWillUnmount() {
    this.cropper.destroy()
    this.setState({ isCropperDestroyed: true })
  }

  setRef = (node) => {
    this.cropperNode = node
  }

  handleSaveButtonClick = async () => {
    const {
      onSave,
      data,
      config: { storeFile: uploadFile },
      t,
    } = this.props

    this.setState({ canCrop: false, errorMessage: null })
    this.cropper.disable()

    const filename = getFilename(data.get('src'))

    const mimeType = mimeTypeMap[filename.toLowerCase().split('.').pop()]

    const blob = dataUriToBlob(this.cropper.getCroppedCanvas().toDataURL(mimeType), mimeType)
    const file = navigator.msSaveBlob
      ? Object.assign(blob, { name: filename })
      : new File([blob], filename, { type: blob.type })

    try {
      const { absoluteDownloadUrl: src, width, height } = await uploadFile(file)
      onSave(data.merge(Immutable.Map({ src, width, height })))
    } catch (error) {
      this.setState({
        canCrop: true,
        errorMessage: t('components.imageUploadComponent.imageField.errorMessages.messageWithOldImage', {
          reason: getErrorReasonMessage(error, t),
        }),
      })
      this.cropper.enable()
    }
  }

  handleCancelButtonClick = () => this.props.onCancel()

  handleZoomButtonClick = (factor) => () => this.cropper.zoom(zoomRatio * factor)

  handleMoveButtonClick = (offsetX, offsetY) => () => this.cropper.move(offsetX, offsetY)

  handleAspectRatioInputChange = (event) => {
    const aspectRatio = Number(event.target.value)
    const { width: cropWidth, height: cropHeight } = this.cropper.setAspectRatio(aspectRatio).getData(true)

    this.setState({ cropWidth, cropHeight, cropAspectRatio: aspectRatio })
  }

  handleCropWidthInputChange = (event) => {
    const newData = { width: getSanitizedCropSizeValue(event.target.value) }

    if (this.state.lockAspectRatioOnSizeInputFieldChange) {
      const { naturalWidth: imageWidth, naturalHeight: imageHeight } = this.cropper.getImageData()
      newData.height = Math.round((newData.width * imageHeight) / imageWidth)
    }

    this.cropper.setData(newData)
  }

  handleCropHeightInputChange = (event) => {
    const newData = { height: getSanitizedCropSizeValue(event.target.value) }

    if (this.state.lockAspectRatioOnSizeInputFieldChange) {
      const { naturalWidth: imageWidth, naturalHeight: imageHeight } = this.cropper.getImageData()
      newData.width = Math.round((newData.height * imageWidth) / imageHeight)
    }

    this.cropper.setData(newData)
  }

  handleAutoAdjustSizeInputAspectRatioToggle = (event) => {
    if (event.type === 'click' || event.key === ' ') {
      this.setState({ lockAspectRatioOnSizeInputFieldChange: !this.state.lockAspectRatioOnSizeInputFieldChange })
    }
  }

  handleCropperReady = () => {
    if (!this.state.isCropperDestroyed) {
      this.setState({ isCropperReady: true, canCrop: true })
    }
  }

  handleCropperCrop = (event) => {
    const cropWidth = Math.round(event.detail.width)
    const cropHeight = Math.round(event.detail.height)

    if (this.state.cropWidth !== cropWidth || this.state.cropHeight !== cropHeight) {
      this.setState({ cropWidth, cropHeight })
    }
  }

  get body() {
    const { config, data } = this.props
    const { lockAspectRatioOnSizeInputFieldChange, cropWidth, cropHeight, cropAspectRatio, errorMessage } = this.state
    const isFreeCroppingDisabled = Boolean(this.state.cropAspectRatio)
    const imageSource = config.imageUrl ? config.imageUrl(data.get('src')) : data.get('src')

    return (
      <>
        {errorMessage && <div className="dali-notification-danger">{errorMessage}</div>}
        <div className="dali-settingslayer-image-crop-container">
          <div className="dali-settingslayer-image-crop-cropper" data-cropper-ready={this.state.isCropperReady || null}>
            <img ref={this.setRef} src={imageSource} alt={data.get('alt')} />
          </div>
          <div className="dali-settingslayer-image-crop-navigation">
            <button
              type="button"
              className="zoom-in"
              onClick={this.handleZoomButtonClick(1)}
              title={this.t('zoomInButton.tooltip')}
            >
              <span>{this.t('zoomInButton.label')}</span>
            </button>
            <button
              type="button"
              className="zoom-out"
              onClick={this.handleZoomButtonClick(-1)}
              title={this.t('zoomOutButton.tooltip')}
            >
              <span>{this.t('zoomOutButton.label')}</span>
            </button>
            <button
              type="button"
              className="move-left"
              onClick={this.handleMoveButtonClick(-1, 0)}
              title={this.t('moveLeftButton.tooltip')}
            >
              <span>{this.t('moveLeftButton.label')}</span>
            </button>
            <button
              type="button"
              className="move-right"
              onClick={this.handleMoveButtonClick(1, 0)}
              title={this.t('moveRightButton.tooltip')}
            >
              <span>{this.t('moveRightButton.label')}</span>
            </button>
            <button
              type="button"
              className="move-up"
              onClick={this.handleMoveButtonClick(0, -1)}
              title={this.t('moveUpButton.tooltip')}
            >
              <span>{this.t('moveUpButton.label')}</span>
            </button>
            <button
              type="button"
              className="move-down"
              onClick={this.handleMoveButtonClick(0, 1)}
              title={this.t('moveDownButton.tooltip')}
            >
              <span>{this.t('moveDownButton.label')}</span>
            </button>
          </div>
        </div>
        <div className="dali-settingslayer-image-crop-settings">
          {Object.keys(aspectRatioMap).map((aspectRatioLabel) => (
            <Fragment key={aspectRatioLabel}>
              <input
                type="radio"
                name={`${this.htmlId}-aspectRatio`}
                id={`${this.htmlId}-aspectRatio-${aspectRatioLabel}`}
                value={aspectRatioMap[aspectRatioLabel]}
                checked={cropAspectRatio === aspectRatioMap[aspectRatioLabel]}
                onChange={this.handleAspectRatioInputChange}
              />
              <label
                htmlFor={`${this.htmlId}-aspectRatio-${aspectRatioLabel}`}
                aria-label={this.t('aspectRatioButton.label', { aspectRatio: aspectRatioLabel })}
              >
                <span />
              </label>
            </Fragment>
          ))}
          <input
            type="radio"
            name="aspectRatio"
            id="aspectRatio-free"
            checked={!isFreeCroppingDisabled}
            onChange={this.handleAspectRatioInputChange}
          />
          <label htmlFor="aspectRatio-free" aria-label={this.t('freeRatioButton.label')}>
            <span />
          </label>
          <div
            className={cc([
              'dali-settingslayer-image-crop-settings-free-ratio',
              {
                disabled: isFreeCroppingDisabled,
              },
            ])}
          >
            <label>
              <input
                type="text"
                value={cropWidth}
                disabled={isFreeCroppingDisabled}
                onChange={this.handleCropWidthInputChange}
              />
              {this.t('widthInputField.label')}
            </label>
            <span
              className="auto-adjust-toggle"
              role="checkbox"
              title={this.t('lockAspectRatioInputField.label')}
              aria-label={this.t('lockAspectRatioInputField.label')}
              aria-checked={lockAspectRatioOnSizeInputFieldChange}
              aria-disabled={isFreeCroppingDisabled}
              tabIndex={isFreeCroppingDisabled ? '-1' : '0'}
              onClick={this.handleAutoAdjustSizeInputAspectRatioToggle}
              onKeyUp={this.handleAutoAdjustSizeInputAspectRatioToggle}
            />
            <label>
              <input
                type="text"
                value={cropHeight}
                disabled={isFreeCroppingDisabled}
                onChange={this.handleCropHeightInputChange}
              />
              {this.t('heightInputField.label')}
            </label>
          </div>
        </div>
      </>
    )
  }

  get footer() {
    return (
      <ButtonRow>
        <Button type="button" variant="cancel" onClick={this.handleCancelButtonClick}>
          {this.t('cancelButton.label')}
        </Button>
        <Button type="button" onClick={this.handleSaveButtonClick} disabled={!this.state.canCrop}>
          {this.t('saveButton.label')}
        </Button>
      </ButtonRow>
    )
  }

  render() {
    return this.props.renderLayout({
      title: this.t('title'),
      body: this.body,
      footer: this.footer,
      onCancel: this.handleCancelButtonClick,
    })
  }
}

export default compose(withI18n('interface'), translate())(LogoCropSettingsRaw)
